/**
 * Drop generation in words (19/10/2026): what today's generation did — the
 * Drop generation tile's status — and when Auto-generate runs next — the
 * Auto-generate tile's —, from the scheduler's status. Danish clock times.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DropGenSchedulerPlanStatus } from '@trafficpulse/shared';

import { inWords } from './window-now';

/** A status dot: grey idle, pulsing green live, green done, amber needs a look. */
export type FactTone = 'muted' | 'live' | 'success' | 'warning';

export interface FactStatus {
  text: string;
  tone: FactTone;
}

const cph = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: 'Europe/Copenhagen' });

/** Today's generation: "Generated 10:41 🇩🇰 · by hand", "Not generated yet today", "Failed today — see the feed". */
export function todayGeneration(s: DropGenSchedulerPlanStatus | undefined): FactStatus {
  const run = s?.todayRun;
  if (!run) return { text: 'Not generated yet today', tone: 'muted' };
  if (run.status === 'FAILED') return { text: 'Failed today — see the feed', tone: 'warning' };
  const who = run.source === 'MANUAL' ? 'by hand' : 'automatically';
  return { text: `Generated ${cph(run.startedAt)} 🇩🇰 · ${who}`, tone: 'success' };
}

/** When Auto-generate runs next: "Next in 3 h 20 min" (tomorrow's once today's is past), or "Due now". */
export function nextGeneration(s: DropGenSchedulerPlanStatus | undefined, now = new Date()): FactStatus | null {
  if (!s || s.state === 'off') return null;
  if (s.state === 'due') return { text: 'Due now — catching up', tone: 'warning' };
  let at = new Date(s.generateAtUtc).getTime();
  while (at <= now.getTime()) at += 86_400_000;
  return { text: `Next in ${inWords(Math.round((at - now.getTime()) / 60_000))}`, tone: 'live' };
}
